import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  ContactFieldDefinition,
  fetchAllTags,
  getContactFieldDefinitions,
  Tag,
} from "../../../helpers/audience_helper";
import {
  apiErrorMessage,
  CaptureForm,
  createForm,
  FormDoiPreview,
  getForm,
  getFormDoiPreview,
  updateForm,
} from "../../../helpers/forms_helper";

type Option = { value: number | string; label: string };

type FormValues = {
  name: string;
  status: "active" | "paused";
  double_opt_in: boolean;
  thank_you_message: string;
  redirect_url: string;
  include_name: boolean;
  include_phone: boolean;
  tag_ids: number[];
  slugs: string[];
};

const defaultValues = (): FormValues => ({
  name: "",
  status: "active",
  double_opt_in: false,
  thank_you_message: "",
  redirect_url: "",
  include_name: true,
  include_phone: false,
  tag_ids: [],
  slugs: [],
});

/**
 * Criar / editar formulário de captura (DOC 03 § 4.13).
 */
const FormCreateEdit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const formId = isNew ? null : Number(id);

  document.title = isNew
    ? t("forms.documentTitleNew")
    : t("forms.documentTitleEdit");

  const helpHref = helpUrlForRoute("/forms", i18n.language || "pt-BR");

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [fieldDefs, setFieldDefs] = useState<ContactFieldDefinition[]>([]);
  const [existing, setExisting] = useState<CaptureForm | null>(null);
  const [doiPreview, setDoiPreview] = useState<FormDoiPreview | null>(null);
  const [doiPreviewLoading, setDoiPreviewLoading] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("forms.error.nameRequired")).max(120),
        status: Yup.mixed<"active" | "paused">()
          .oneOf(["active", "paused"])
          .required(),
        double_opt_in: Yup.boolean(),
        thank_you_message: Yup.string().max(5000),
        redirect_url: Yup.string()
          .trim()
          .max(2048)
          .test("url-or-empty", t("forms.error.redirectUrl"), (value) => {
            if (!value) return true;
            try {
              const u = new URL(value);
              return u.protocol === "http:" || u.protocol === "https:";
            } catch {
              return false;
            }
          }),
        include_name: Yup.boolean(),
        include_phone: Yup.boolean(),
        tag_ids: Yup.array().of(Yup.number()),
        slugs: Yup.array().of(Yup.string()),
      }),
    [t]
  );

  const formik = useFormik<FormValues>({
    initialValues: defaultValues(),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setError(null);
      const payload = {
        name: values.name.trim(),
        status: values.status,
        double_opt_in: values.double_opt_in,
        thank_you_message: values.thank_you_message.trim() || null,
        redirect_url: values.redirect_url.trim() || null,
        tag_ids: values.tag_ids,
        field_slugs: {
          include_name: values.include_name,
          include_phone: values.include_phone,
          slugs: values.slugs,
        },
      };
      try {
        if (formId) {
          await updateForm(formId, payload);
          toast.success(t("forms.success.updated"));
          navigate(`/forms/${formId}/embed`);
        } else {
          const res = await createForm(payload);
          toast.success(t("forms.success.created"));
          navigate(`/forms/${res.data.id}/embed`);
        }
      } catch (e) {
        setError(apiErrorMessage(e, t("forms.error.save")));
      } finally {
        setSaving(false);
      }
    },
  });

  const loadLookups = useCallback(async () => {
    try {
      const [tagList, defsRes] = await Promise.all([
        fetchAllTags(),
        getContactFieldDefinitions({ per_page: 100 }),
      ]);
      setTags(tagList);
      setFieldDefs(defsRes.data || []);
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.load")));
    }
  }, [t]);

  const loadForm = useCallback(async () => {
    if (!formId || Number.isNaN(formId)) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getForm(formId);
      const form = res.data;
      setExisting(form);
      formik.setValues({
        name: form.name,
        status: form.status,
        double_opt_in: !!form.double_opt_in,
        thank_you_message: form.thank_you_message || "",
        redirect_url: form.redirect_url || "",
        include_name: form.field_slugs?.include_name ?? true,
        include_phone: form.field_slugs?.include_phone ?? false,
        tag_ids: form.tag_ids || [],
        slugs: form.field_slugs?.slugs || [],
      });
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.load")));
    } finally {
      setLoading(false);
    }
    // formik.setValues é estável o bastante; evita loop com formik no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, t]);

  useEffect(() => {
    void loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!isNew) {
      void loadForm();
    }
  }, [isNew, loadForm]);

  // Prévia real do e-mail DOI — só após salvar (precisa de id no backend).
  useEffect(() => {
    if (!formId || !formik.values.double_opt_in) {
      setDoiPreview(null);
      return;
    }
    setDoiPreviewLoading(true);
    getFormDoiPreview(formId)
      .then((res) => setDoiPreview(res.data))
      .catch(() => setDoiPreview(null))
      .finally(() => setDoiPreviewLoading(false));
  }, [formId, formik.values.double_opt_in]);

  const tagOptions: Option[] = useMemo(
    () => tags.map((tag) => ({ value: tag.id, label: tag.name })),
    [tags]
  );

  const slugOptions: Option[] = useMemo(
    () =>
      fieldDefs.map((def) => ({
        value: def.slug,
        label: def.label || def.slug,
      })),
    [fieldDefs]
  );

  const selectedTags = tagOptions.filter((o) =>
    formik.values.tag_ids.includes(Number(o.value))
  );

  const selectedSlugs = slugOptions.filter((o) =>
    formik.values.slugs.includes(String(o.value))
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={
            isNew
              ? t("forms.createTitle")
              : t("forms.editTitle", { name: existing?.name || "" })
          }
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("forms.help")} />
            ) : undefined
          }
        />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void formik.handleSubmit();
                  }}
                >
                  <FormGroup>
                    <Label for="name">{t("forms.field.name")}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.name && formik.errors.name)}
                    />
                    <FormFeedback>{formik.errors.name}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="status">{t("forms.field.status")}</Label>
                        <Input
                          id="status"
                          name="status"
                          type="select"
                          value={formik.values.status}
                          onChange={formik.handleChange}
                        >
                          <option value="active">
                            {t("forms.status.active")}
                          </option>
                          <option value="paused">
                            {t("forms.status.paused")}
                          </option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mt-md-4">
                        <div className="form-check form-switch">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="double_opt_in"
                            checked={formik.values.double_opt_in}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "double_opt_in",
                                e.target.checked
                              )
                            }
                          />
                          <Label
                            className="form-check-label"
                            for="double_opt_in"
                          >
                            {t("forms.field.doubleOptIn")}
                          </Label>
                        </div>
                        <small className="text-muted d-block mt-1">
                          {t("forms.field.doubleOptInHint")}
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  {formik.values.double_opt_in ? (
                    <div className="mb-3">
                      <strong className="d-block mb-2">
                        {t("forms.doiPreview.title")}
                      </strong>
                      {doiPreviewLoading ? (
                        <div className="text-muted small py-2">
                          <Spinner size="sm" className="me-2" />
                          {t("forms.doiPreview.loading")}
                        </div>
                      ) : doiPreview ? (
                        <>
                          <p className="small text-muted mb-2">
                            <em>{doiPreview.subject}</em>
                          </p>
                          <iframe
                            title={t("forms.doiPreview.title")}
                            sandbox=""
                            srcDoc={doiPreview.html}
                            className="w-100 border rounded bg-white"
                            style={{ minHeight: 320, maxHeight: 420 }}
                          />
                        </>
                      ) : (
                        <Alert color="info" className="mb-0">
                          <p className="mb-1 small">
                            <em>{t("forms.doiPreview.subjectSample")}</em>
                          </p>
                          <p className="small text-muted mb-0">
                            {t("forms.doiPreview.bodySample")}
                          </p>
                        </Alert>
                      )}
                    </div>
                  ) : null}

                  <FormGroup>
                    <Label for="thank_you_message">
                      {t("forms.field.thankYou")}
                    </Label>
                    <Input
                      id="thank_you_message"
                      name="thank_you_message"
                      type="textarea"
                      rows={3}
                      value={formik.values.thank_you_message}
                      onChange={formik.handleChange}
                    />
                    {formik.values.double_opt_in ? (
                      <small className="text-muted d-block mt-1">
                        {t("forms.field.thankYouDoiHint")}
                      </small>
                    ) : null}
                  </FormGroup>

                  <FormGroup>
                    <Label for="redirect_url">
                      {t("forms.field.redirectUrl")}
                    </Label>
                    <Input
                      id="redirect_url"
                      name="redirect_url"
                      placeholder="https://"
                      value={formik.values.redirect_url}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.redirect_url &&
                          formik.errors.redirect_url
                        )
                      }
                    />
                    <FormFeedback>{formik.errors.redirect_url}</FormFeedback>
                    <small className="text-muted">
                      {t("forms.field.redirectUrlHint")}
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label>{t("forms.field.tags")}</Label>
                    <Select
                      isMulti
                      options={tagOptions}
                      value={selectedTags}
                      onChange={(opts: readonly Option[] | null) =>
                        formik.setFieldValue(
                          "tag_ids",
                          (opts || []).map((o: Option) => Number(o.value))
                        )
                      }
                      placeholder={t("forms.field.tagsPlaceholder")}
                      classNamePrefix="select"
                    />
                    <small className="text-muted">
                      {t("forms.field.tagsHint")}
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label className="d-block">
                      {t("forms.field.nativeFields")}
                    </Label>
                    <div className="form-check form-check-inline">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="include_name"
                        checked={formik.values.include_name}
                        onChange={(e) =>
                          formik.setFieldValue("include_name", e.target.checked)
                        }
                      />
                      <Label className="form-check-label" for="include_name">
                        {t("forms.field.includeName")}
                      </Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="include_phone"
                        checked={formik.values.include_phone}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "include_phone",
                            e.target.checked
                          )
                        }
                      />
                      <Label className="form-check-label" for="include_phone">
                        {t("forms.field.includePhone")}
                      </Label>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>{t("forms.field.customFields")}</Label>
                    <Select
                      isMulti
                      options={slugOptions}
                      value={selectedSlugs}
                      onChange={(opts: readonly Option[] | null) =>
                        formik.setFieldValue(
                          "slugs",
                          (opts || []).map((o: Option) => String(o.value))
                        )
                      }
                      placeholder={
                        slugOptions.length
                          ? t("forms.field.customFieldsPlaceholder")
                          : t("forms.field.noCustomFields")
                      }
                      isDisabled={slugOptions.length === 0}
                      classNamePrefix="select"
                    />
                    {slugOptions.length === 0 && (
                      <small className="text-muted d-block mt-1">
                        <Link to="/settings/contact-fields">
                          {t("forms.field.manageFields")}
                        </Link>
                      </small>
                    )}
                  </FormGroup>

                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <Spinner size="sm" />
                      ) : isNew ? (
                        t("forms.saveCreate")
                      ) : (
                        t("forms.save")
                      )}
                    </Button>
                    <Button
                      color="light"
                      type="button"
                      tag={Link}
                      to="/forms"
                      disabled={saving}
                    >
                      {t("forms.cancel")}
                    </Button>
                    {!isNew && formId && (
                      <Button
                        color="soft-info"
                        type="button"
                        tag={Link}
                        to={`/forms/${formId}/embed`}
                      >
                        {t("forms.embed")}
                      </Button>
                    )}
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer autoClose={3000} />
    </div>
  );
};

export default FormCreateEdit;
